import { Form, Formik, FormikProps, useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { Switch } from "@mui/material";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import {
  CorporateActionDetails,
  SplitGrantDetails,
} from "../../types/CompanyProfile";
import { CollapsibleComponent } from "../../components/shared/Collapsible";
import {
  useCreateCorporateAction,
  useGetAllGrantDetailsForCorporateActions,
} from "../../queries/onBoarding";
import { formatDisplayDate } from "../../utils/date";
import { ConversionMethodology } from "../../types/Exercise";
import GenericFilterBar, { GenericFilterType } from "./utils/Filters";
import { NameAndId, UploadedFile } from "../../types/Transaction";
import { SwitchButton } from "../../components/shared/SwitchButton";
import { Select } from "../../components/shared/Select";
import { TooltipProps } from "../../components/shared/Tooltip";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import { StrikePriceType } from "../../types/EsopPlan";
import { useEsopPlans } from "../../queries";

export function AddOrEditSplitDetails({
  corporateAction,
  mode,
  onClose,
  setDialog,
}: {
  corporateAction?: CorporateActionDetails;
  mode: "AddSplit" | "EditSplit" | "ApproveSplit";
  onClose: () => void;
  setDialog: Function;
}) {
  const currencyType = getCurrencyType();
  const initialValues: CorporateActionDetails = {
    id: corporateAction?.id,
    name: corporateAction?.name || "",
    date: corporateAction?.date
      ? new Date(corporateAction.date).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    ratio: corporateAction?.ratio || 1,
    type: corporateAction?.type || "SPLIT",
    state: corporateAction?.state || "DRAFT",
    settings: corporateAction?.settings || {
      useGrantRelatedDocumentTemplate: true,
    },
    note: corporateAction?.note || "",
    roundingMethodology:
      corporateAction?.roundingMethodology || ConversionMethodology.ROUND_DOWN,
    metaData: corporateAction?.metaData || {},
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Split Name"),
    date: Yup.date().required("Split Date"),
    ratio: Yup.number()
      .positive()
      .required("Split Ratio Required")
      .moreThan(1, "Split Ratio must be greater than 1"),
    settings: Yup.object().shape({
      templateFile: Yup.mixed().test(
        "templateFile-required",
        "Split Letter Template is required when letter generation is enabled",
        // eslint-disable-next-line
        function (value) {
          const { generateTransactionDocuments } = this.parent;
          if (generateTransactionDocuments && !value) {
            return false;
          }
          return true;
        }
      ),
    }),
  });

  const [accordionOpened, setAccordionOpened] = useState<{
    [key: string]: boolean;
  }>({
    basicDetails: false,
    settings: true,
    documents: true,
    gridData: false,
  });

  const [disableForm, setDisableForm] = useState(false);
  const { mutate: createCorporateAction } = useCreateCorporateAction();

  useEffect(() => {
    setDisableForm(corporateAction?.state === "APPROVED");
  }, [corporateAction?.state]);

  const { data: splitGrantDetails } = useGetAllGrantDetailsForCorporateActions(
    corporateAction?.id || "",
    corporateAction?.state || "DRAFT",
    true
  );
  const {
    data: esopPlans,
    isPlaceholderData,
    isFetched,
  } = useEsopPlans(!!corporateAction?.id && corporateAction?.state === "DRAFT");
  const [companiesPoolSize, setCompaniesPoolSize] = useState<{
    [key: string]: number;
  }>({});
  const [plansEffected, setPlansEffected] = useState<string[]>([]);

  useEffect(() => {
    if (splitGrantDetails && splitGrantDetails.length > 0) {
      setCompaniesPoolSize({
        ...companiesPoolSize,
        existingTotalPoolSize: splitGrantDetails[0].currentPoolShares || 0,
      });
    }
  }, [splitGrantDetails]);

  const gridApi = useRef<GridApi | null>(null);

  const [rowData, setRowData] = useState<SplitGrantDetails[]>([]);

  useEffect(() => {
    if (splitGrantDetails) {
      setRowData(splitGrantDetails);
    }
  }, [splitGrantDetails]);

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
  };

  const handleAccordionClick = (section: string) => {
    setAccordionOpened((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const handleSubmit = (values: CorporateActionDetails) => {
    if (disableForm) return;
    setIsSaveLoading(true);
    createCorporateAction(values, {
      onSuccess: (data) => {
        toast.success("Split saved successfully");
        onClose();
        setIsSaveLoading(false);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.errorRaw || "Error saving Split");
        setIsSaveLoading(false);
      },
    });
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (isFetched && esopPlans && plansEffected.length > 0) {
      let totalPoolSize = 0;
      esopPlans.forEach((plan) => {
        if (plansEffected.includes(plan.esopPlanId || "")) {
          totalPoolSize +=
            ((plan.totalShares || 0) - (plan.sharesManagedByTrust || 0)) *
            (formik.values.ratio || 0);
        } else {
          totalPoolSize +=
            (plan.totalShares || 0) - (plan.sharesManagedByTrust || 0);
        }
      });
      setCompaniesPoolSize({
        ...companiesPoolSize,
        minimumPoolSizeRequired: totalPoolSize,
      });
    }
  }, [esopPlans, plansEffected, formik.values.ratio]);

  function ApproveTooltip(props: TooltipProps) {
    const { text, children, _className, disabled = false } = props;
    return (
      <div className="relative group ">
        {children}
        <div
          className={`absolute bottom-0 right-0 flex-col items-center hidden mb-6 rounded-md left-30 ${
            disabled ? "" : "group-hover:flex"
          }`}
        >
          <div
            className={`relative z-20 p-2 text-sm text-black shadow-lg bg-[#f6f9fb] ${_className}`}
          >
            {text}
          </div>
          <div className="w-3 h-3 -mt-2 ml-40 rotate-45 bg-[#f6f9fb]"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-10 text-lg font-medium border-b py-7">
          <h6 className="flex justify-between">
            <p className="font-bold">Split</p>
            <span onClick={onClose} className="cursor-pointer">
              X
            </span>
          </h6>
        </div>
        <VStack className="w-full px-10 py-5 gap-9">
          <VStack>
            <VStack className="gap-6">
              <CollapsibleComponent
                label={"Split Event Details"}
                labelDetail={""}
                isOpen={accordionOpened.basicDetails}
                onClick={() => handleAccordionClick("basicDetails")}
              >
                <VStack className="gap-3">
                  <HStack className="justify-between gap-4">
                    <VStack className="w-1/2">
                      <VStack className="">
                        <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                          Split Name
                        </Label>
                        <VStack className="gap-1 w-full">
                          <Input
                            type="text"
                            {...formik.getFieldProps("name")}
                            disabled={disableForm}
                            className={`${
                              disableForm ? "cursor-not-allowed" : ""
                            }`}
                          />
                          <div>
                            {formik.touched.name && formik.errors.name && (
                              <Error text={formik.errors.name} />
                            )}
                          </div>
                        </VStack>
                      </VStack>
                    </VStack>
                    <VStack className="w-1/2">
                      <VStack className="">
                        <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                          Split Date
                        </Label>
                        <VStack className="gap-1 w-full">
                          <Input
                            type="date"
                            {...formik.getFieldProps("date")}
                            disabled={disableForm}
                            className={`${
                              disableForm ? "cursor-not-allowed" : ""
                            }`}
                            onChange={(e) => {
                              const selectedDate = e.target.value;
                              formik.setFieldValue("date", selectedDate);
                              formik.setFieldValue(
                                "metaData.filters.date",
                                selectedDate
                              );
                            }}
                          />
                          <div>
                            {formik.touched.date && formik.errors.date && (
                              <Error text={formik.errors.date} />
                            )}
                          </div>
                        </VStack>
                      </VStack>
                    </VStack>
                  </HStack>
                  <HStack className="space-between gap-4">
                    <VStack className="w-1/2">
                      <VStack className="">
                        <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                          Split Ratio
                        </Label>
                        <VStack className="gap-1 w-full">
                          <HStack className="items-center form-input px-0 py-0">
                            <p className="text-sm font-normal pl-3 whitespace-nowrap">
                              1 :
                            </p>
                            <Input
                              type="number"
                              {...formik.getFieldProps("ratio")}
                              disabled={disableForm}
                              className={`${
                                disableForm ? "cursor-not-allowed" : ""
                              } pl-2`}
                            />
                          </HStack>
                          {formik.touched.ratio && formik.errors.ratio && (
                            <Error text={formik.errors.ratio} />
                          )}
                        </VStack>
                      </VStack>
                      <HStack className="justify-end pt-1">
                        {formik.values.ratio > 0 && (
                          <p className="text-xs font-semi-medium text-gray-light">
                            {formik.values.ratio} split share(s) for every 1
                            Equivalent share
                          </p>
                        )}
                      </HStack>
                    </VStack>
                    <VStack className="w-1/2">
                      <VStack className="">
                        <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                          Methodology
                        </Label>
                        <VStack className="gap-1 w-full">
                          <Select
                            options={[
                              ConversionMethodology.ROUND_DOWN,
                              ConversionMethodology.ROUND_UP,
                            ]}
                            textGetter={(e) => e.replaceAll("_", " ")}
                            {...formik.getFieldProps("roundingMethodology")}
                          />
                        </VStack>
                      </VStack>
                    </VStack>
                  </HStack>
                  <HStack className="space-between gap-4 w-1/2">
                    <VStack className="w-full">
                      <VStack className="flex-1">
                        <Label className="mr-10 text-sm font-normal whitespace-nowrap">
                          Notes
                        </Label>
                        <VStack className="gap-1 ">
                          <TextArea
                            {...formik.getFieldProps("note")}
                            disabled={disableForm}
                            className={`${
                              disableForm ? "cursor-not-allowed" : ""
                            }`}
                          />
                        </VStack>
                        <VStack className="flex-1"></VStack>
                      </VStack>
                    </VStack>
                  </HStack>
                </VStack>
              </CollapsibleComponent>
              <CollapsibleComponent
                label={"Grants Affected by Split"}
                labelDetail={""}
                isOpen={accordionOpened.gridData}
                onClick={() => handleAccordionClick("gridData")}
              >
                <GridData
                  splitGrantDetails={rowData}
                  onGridReady={onGridReady}
                  formik={formik}
                  currencyType={currencyType}
                  setPlansEffected={setPlansEffected}
                />
              </CollapsibleComponent>
              <CollapsibleComponent
                label={"Split Notification Settings"}
                labelDetail={""}
                isOpen={accordionOpened.settings}
                onClick={() => handleAccordionClick("settings")}
              >
                <SplitNotificationSettings
                  formik={formik}
                  disableForm={disableForm}
                />
              </CollapsibleComponent>
              <CollapsibleComponent
                label={"Split Documents"}
                labelDetail={""}
                isOpen={accordionOpened.documents}
                onClick={() => handleAccordionClick("documents")}
              >
                <SplitDocumentsSection
                  formik={formik}
                  disableForm={disableForm}
                  mode={mode}
                />
              </CollapsibleComponent>
              <SplitWarningNotes />
              {companiesPoolSize &&
                companiesPoolSize.existingTotalPoolSize &&
                companiesPoolSize.minimumPoolSizeRequired &&
                companiesPoolSize.existingTotalPoolSize <
                  companiesPoolSize.minimumPoolSizeRequired &&
                corporateAction?.state === "DRAFT" && (
                  <VStack className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                    <HStack className="items-start gap-3">
                      <Icon
                        icon="material-symbols:warning-rounded"
                        className="text-red-500 flex-shrink-0 mt-0.5"
                        width={24}
                        height={24}
                      />
                      <VStack className="gap-2 flex-1">
                        <h4 className="text-sm font-semibold text-red-900">
                          Insufficient Pool Size
                        </h4>
                        <p className="text-sm text-red-800">
                          The split operation requires additional pool capacity.
                          Please ensure sufficient pool size before proceeding
                          with the split approval.
                        </p>
                        <HStack className="gap-6 mt-2 p-3 bg-white rounded border border-red-200">
                          <VStack className="gap-1">
                            <span className="text-xs text-gray-600 font-medium">
                              Current Pool Size
                            </span>
                            <span className="text-base font-semibold text-gray-900">
                              {companiesPoolSize.existingTotalPoolSize.toLocaleString(
                                currencyType
                              )}
                            </span>
                          </VStack>
                          <Icon
                            icon="material-symbols:arrow-forward"
                            className="text-red-400 mt-4"
                            width={20}
                          />
                          <VStack className="gap-1">
                            <span className="text-xs text-gray-600 font-medium">
                              Required Pool Size
                            </span>
                            <span className="text-base font-semibold text-red-600">
                              {formik.values.roundingMethodology ===
                              ConversionMethodology.ROUND_DOWN
                                ? Math.floor(
                                    companiesPoolSize.minimumPoolSizeRequired
                                  ).toLocaleString(currencyType)
                                : companiesPoolSize.minimumPoolSizeRequired.toLocaleString(
                                    currencyType
                                  )}
                            </span>
                          </VStack>
                          <VStack className="gap-1 ml-4">
                            <span className="text-xs text-gray-600 font-medium">
                              Additional Needed
                            </span>
                            <span className="text-base font-semibold text-red-700">
                              {(formik.values.roundingMethodology ===
                              ConversionMethodology.ROUND_DOWN
                                ? Math.floor(
                                    companiesPoolSize.minimumPoolSizeRequired -
                                      companiesPoolSize.existingTotalPoolSize
                                  )
                                : companiesPoolSize.minimumPoolSizeRequired -
                                  companiesPoolSize.existingTotalPoolSize
                              ).toLocaleString(currencyType)}
                            </span>
                          </VStack>
                        </HStack>
                        <p className="text-xs text-red-700 mt-1">
                          <strong>Action Required:</strong> Increase your
                          company pool size by at least{" "}
                          {(formik.values.roundingMethodology ===
                          ConversionMethodology.ROUND_DOWN
                            ? Math.floor(
                                companiesPoolSize.minimumPoolSizeRequired -
                                  companiesPoolSize.existingTotalPoolSize
                              )
                            : companiesPoolSize.minimumPoolSizeRequired -
                              companiesPoolSize.existingTotalPoolSize
                          ).toLocaleString(currencyType)}{" "}
                          shares to proceed with this split.
                        </p>
                      </VStack>
                    </HStack>
                  </VStack>
                )}
              <HStack className="justify-between mt-4">
                <ButtonSecondary className="h-8 px-2 py-1" onClick={onClose}>
                  Cancel
                </ButtonSecondary>
                <HStack className="gap-5">
                  <ButtonPrimary
                    type="submit"
                    loading={isSaveLoading}
                    disabled={
                      disableForm ||
                      isSaveLoading ||
                      JSON.stringify(initialValues) ===
                        JSON.stringify(formik.values)
                    }
                    onClick={() => formik.handleSubmit()}
                  >
                    Save
                  </ButtonPrimary>
                  {mode === "EditSplit" && corporateAction?.state === "DRAFT" && (
                    <ApproveTooltip
                      text={
                        JSON.stringify(initialValues) !==
                        JSON.stringify(formik.values)
                          ? "Please save details to approve"
                          : companiesPoolSize.existingTotalPoolSize <
                            companiesPoolSize.minimumPoolSizeRequired
                          ? "Insufficient pool size to approve the split"
                          : "Approving will create the Split Grants"
                      }
                      _className="whitespace-nowrap"
                    >
                      <ButtonPrimary
                        onClick={() => {
                          if (
                            JSON.stringify(initialValues) ===
                            JSON.stringify(formik.values)
                          ) {
                            setDialog({
                              open: true,
                              data: formik.values,
                              mode: "ApproveSplit",
                            });
                          }
                        }}
                        disabled={
                          JSON.stringify(initialValues) !==
                            JSON.stringify(formik.values) ||
                          disableForm ||
                          companiesPoolSize.existingTotalPoolSize <
                            companiesPoolSize.minimumPoolSizeRequired
                        }
                      >
                        Approve
                      </ButtonPrimary>
                    </ApproveTooltip>
                  )}
                </HStack>
              </HStack>
            </VStack>
          </VStack>
        </VStack>
      </div>
    </>
  );
}

function SplitWarningNotes() {
  const notes = [
    `All grants up to and including the split date will be taken into account.`,
    `If filters are not selected, all eligible grants will be selected by default.`,
    `The selected rounding methodology will be applied to all post-split calculations and will be used for all future transactions on affected grants.`,
    `Ensure the pool size is updated to accommodate additional shares created by the split.`,
    `If a plan is managed by trust, factor in trust-managed shares while updating the pool size.`,
    `Strike prices will be adjusted inversely for "Share Price" type grants (divided by split ratio).`,
    `Post-split options will remain the same, but converted shares and active shares will be recalculated based on the split ratio.`,
    `All historical and future share-related transactions or events (including exercises and sales) will be updated or calculated in accordance with the split ratio.`,
    `Once the split is completed, the respective plan’s conversion ratio will be updated accordingly.`,
    `Grants excluded from the split will continue to follow the old conversion ratio, while any new grants issued after the split will follow the revised conversion ratio of corresponding plan`,
  ];

  return (
    <VStack className="p-2 text-black-501 bg-yellow-100 rounded">
      <HStack className="pt-0 justify-start">
        <HStack className="gap-1 pl-1 items-center">
          <Icon
            icon="material-symbols:info-outline-rounded"
            height={18}
            width={18}
            className="w-full text-xs font-medium text-black-501 rounded"
          />
          <p className="text-sm">Note </p>
        </HStack>
      </HStack>
      <Box className="pl-5 pt-2 pb-1">
        <ul className="list-disc">
          {notes.map((note, index) => (
            <li key={index} className="text-xs pt-0.5">
              {note}
            </li>
          ))}
        </ul>
      </Box>
    </VStack>
  );
}

function GridData({
  splitGrantDetails,
  onGridReady,
  formik,
  currencyType,
  setPlansEffected,
}: {
  splitGrantDetails: SplitGrantDetails[];
  onGridReady: any;
  formik: FormikProps<CorporateActionDetails>;
  currencyType?: string;
  setPlansEffected: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  function applyRoundingComponent({
    value,
    methodology,
  }: {
    value: number;
    methodology: string;
  }) {
    const roundingValue =
      methodology === ConversionMethodology.ROUND_DOWN
        ? Math.floor(value)
        : Math.ceil(value);

    const valueIsDecimal = value % 1 !== 0;

    return (
      <VStack className=" items-center  p-0.5">
        <p className="text-sm ">{roundingValue.toLocaleString(currencyType)}</p>

        {valueIsDecimal && (
          <p className="text-xs text-gray-500">
            (
            {Number(value.toFixed(4)).toLocaleString(currencyType, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            })}
            )
          </p>
        )}
      </VStack>
    );
  }
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Grant ID".toUpperCase(),
        field: "identifier",
        sortable: true,
        filter: true,
        minWidth: 120,
      },
      {
        headerName: "Holder Name".toUpperCase(),
        field: "employeeName",
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: "Options Before Split".toUpperCase(),
        field: "optionsBeforeSplit",
        sortable: true,
        filter: true,
        valueFormatter: (params) =>
          Math.floor(params.value).toLocaleString(currencyType),
        minWidth: 150,
      },
      {
        headerName: "Options After Split".toUpperCase(),
        field: "optionsAfterSplit",
        sortable: true,
        filter: true,
        valueFormatter: (params) =>
          Math.floor(params.value).toLocaleString(currencyType),
        minWidth: 150,
      },
      {
        headerName: "Pre Split Strike Price".toUpperCase(),
        field: "strikePrice",
        sortable: true,
        filter: true,
        valueFormatter: (params) =>
          params.value
            ? formatCurrency(
                Number(params.value.toFixed(2)),
                currencyType || ""
              )
            : "",
        minWidth: 150,
      },
      {
        headerName: "Post Split Strike Price".toUpperCase(),
        field: "postSplitStrikePrice",
        sortable: true,
        filter: true,
        valueFormatter: (params) =>
          params.value
            ? formatCurrency(
                Number(params.value.toFixed(2)),
                currencyType || ""
              )
            : "",
        minWidth: 150,
      },
      {
        headerName: "Strike Price Type".toUpperCase(),
        field: "strikePriceType",
        sortable: true,
        filter: true,
        valueFormatter: (params) =>
          params.value ? params.value.replaceAll("_", " ") : "",
        minWidth: 150,
      },
      {
        headerName:
          "Pre-Split Equivalent Shares (Granted Options)".toUpperCase(),
        field: "preSplitGrantedShares",
        sortable: true,
        headerTooltip:
          "Number of equivalent shares before the split for granted options as on today",
        filter: true,
        cellRenderer: (params: any) =>
          applyRoundingComponent({
            value: params.data.preSplitGrantedShares || 0,
            methodology: params.data.conversionMethodology || "ROUND_DOWN",
          }),
        minWidth: 180,
      },
      {
        headerName:
          "Post-Split Equivalent Shares (Granted Options)".toUpperCase(),
        field: "postSplitGrantedShares",
        sortable: true,
        filter: true,
        headerTooltip:
          "Number of equivalent shares after the split for granted options",

        cellRenderer: (params: any) =>
          applyRoundingComponent({
            value:
              (params.data.preSplitGrantedShares || 0) *
              (formik.values.ratio || 1),
            methodology: formik.values.roundingMethodology || "ROUND_DOWN",
          }),
        minWidth: 180,
      },
      {
        headerName: "Active Options".toUpperCase(),
        field: "outstandingOptions",
        sortable: true,
        headerTooltip:
          "Active options as of today (granted minus lapsed, forfeited, exercised, and cashed out)",
        filter: true,
        valueFormatter: (params) =>
          Math.floor(params.value).toLocaleString(currencyType),
        minWidth: 150,
      },
      {
        headerName:
          "Pre-Split Equivalent Shares (Active Options)".toUpperCase(),
        field: "preSplitOutstandingShares",
        sortable: true,
        filter: true,
        cellRenderer: (params: any) =>
          applyRoundingComponent({
            value: params.data.preSplitOutstandingShares || 0,
            methodology: params.data.conversionMethodology || "ROUND_DOWN",
          }),
        minWidth: 180,
      },
      {
        headerName:
          "Post-Split Equivalent Shares (Active Options)".toUpperCase(),
        field: "postSplitOutstandingShares",
        sortable: true,
        filter: true,
        cellRenderer: (params: any) =>
          applyRoundingComponent({
            value:
              (params.data.postSplitOutstandingShares || 0) *
              (formik.values.ratio || 1),
            methodology: formik.values.roundingMethodology || "ROUND_DOWN",
          }),
        minWidth: 180,
      },
      {
        headerName: "Pre-Split Active Shares".toUpperCase(),
        field: "preSplitActiveShares",
        sortable: true,
        filter: true,
        headerTooltip:
          "Total active shares (exercised and unsold) before the split as on today",
        cellRenderer: (params: any) =>
          applyRoundingComponent({
            value: params.data.preSplitActiveShares || 0,
            methodology: params.data.conversionMethodology || "ROUND_DOWN",
          }),
        minWidth: 180,
      },
      {
        headerName: "Post-Split Active Shares".toUpperCase(),
        field: "postSplitActiveShares",
        sortable: true,
        filter: true,
        headerTooltip:
          "Total active shares (exercised and unsold) after the split",

        cellRenderer: (params: any) =>
          applyRoundingComponent({
            value:
              (params.data.preSplitActiveShares || 0) *
              (formik.values.ratio || 1),
            methodology: formik.values.roundingMethodology || "ROUND_DOWN",
          }),
        minWidth: 180,
      },
      {
        headerName: "Grant Date".toUpperCase(),
        field: "dateOfGrant",
        valueFormatter: (params) =>
          params.value ? formatDisplayDate(params.value) : "",
        sortable: true,
        filter: true,
        minWidth: 150,
      },
    ],
    [currencyType, formik.values.ratio, formik.values.roundingMethodology]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      cellStyle: (params) => {
        const baseStyle = { paddingTop: "15px", lineHeight: "20px" };
        if (params.node.rowPinned) {
          return {
            ...baseStyle,
            backgroundColor: "#f0f0f0",
            lineHeight: "30px",
            paddingTop: "5px",
          };
        }
        return baseStyle;
      },
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const [filters, setFilters] = useState<GenericFilterType[]>([]);
  const [filteredRowData, setFilteredRowData] = useState<SplitGrantDetails[]>(
    []
  );

  useEffect(() => {
    const uniqueSubsidiaryIds: NameAndId[] = [];
    const uniquePlanIds: NameAndId[] = [];
    const employmentStatusOptions: string[] = [];
    const employmentTypesOptions: string[] = [];
    const planTypesOptions: string[] = [];
    const grantStateOptions: string[] = [];
    const newFilters: GenericFilterType[] = [];
    for (const grant of splitGrantDetails) {
      if (
        grant.planId &&
        grant.planName &&
        !uniquePlanIds.find((plan) => plan.id === grant.planId)
      ) {
        uniquePlanIds.push({ id: grant.planId, name: grant.planName });
      }
      if (
        grant.subsidiaryCompanyId &&
        grant.subsidiaryCompanyName &&
        !uniqueSubsidiaryIds.find(
          (subsidiary) => subsidiary.id === grant.subsidiaryCompanyId
        )
      ) {
        if (
          grant.companyId &&
          uniqueSubsidiaryIds.some(
            (subsidiary) => subsidiary.id === grant.companyId
          )
        ) {
          uniqueSubsidiaryIds.push({
            id: grant.companyId,
            name: grant.companyName,
          });
        }
        uniqueSubsidiaryIds.push({
          id: grant.subsidiaryCompanyId,
          name: grant.subsidiaryCompanyName,
        });
      }
      if (
        grant.employeeStatus &&
        !employmentStatusOptions.includes(grant.employeeStatus)
      ) {
        employmentStatusOptions.push(grant.employeeStatus);
      }
      if (
        grant.employmentType &&
        !employmentTypesOptions.includes(grant.employmentType)
      ) {
        employmentTypesOptions.push(grant.employmentType);
      }
      if (grant.planType && !planTypesOptions.includes(grant.planType)) {
        planTypesOptions.push(grant.planType);
      }
      if (grant.grantState && !grantStateOptions.includes(grant.grantState)) {
        grantStateOptions.push(grant.grantState);
      }
    }
    if (uniquePlanIds.length > 0) {
      newFilters.push({
        label: "Plans",
        id: "planIds",
        options: uniquePlanIds,
        type: "multiCheckboxNameAndId",
        value: formik.values.metaData?.filters?.planIds || [],
        onChange: (selectedValues: NameAndId[]) => {
          formik.setFieldValue("metaData.filters.planIds", selectedValues);
        },
      });
    }
    if (uniqueSubsidiaryIds.length > 0) {
      newFilters.push({
        label: "Companies",
        id: "subsidiaryIds",
        options: uniqueSubsidiaryIds,
        type: "multiCheckboxNameAndId",
        value: formik.values.metaData?.filters?.subsidiaryIds || [],
        onChange: (selectedValues: NameAndId[]) => {
          formik.setFieldValue(
            "metaData.filters.subsidiaryIds",
            selectedValues
          );
        },
      });
    }
    if (employmentStatusOptions.length > 0) {
      newFilters.push({
        label: "Employment Status",
        id: "employmentStatus",
        options: employmentStatusOptions,
        type: "multiCheckbox",
        value: formik.values.metaData?.filters?.employmentStatus || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue(
            "metaData.filters.employmentStatus",
            selectedValues
          );
        },
      });
    }
    if (employmentTypesOptions.length > 0) {
      newFilters.push({
        label: "Employment Type",
        id: "employmentType",
        options: employmentTypesOptions,
        type: "multiCheckbox",
        value: formik.values.metaData?.filters?.employmentType || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue(
            "metaData.filters.employmentType",
            selectedValues
          );
        },
      });
    }
    if (planTypesOptions.length > 0) {
      newFilters.push({
        label: "Plan Type",
        id: "planType",
        options: planTypesOptions,
        type: "multiCheckbox",
        value: formik.values.metaData?.filters?.planType || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue("metaData.filters.planType", selectedValues);
        },
      });
    }
    if (grantStateOptions.length > 0) {
      newFilters.push({
        label: "Grant State",
        id: "grantState",
        options: grantStateOptions,
        type: "multiCheckbox",
        value: formik.values.metaData?.filters?.grantState || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue("metaData.filters.grantState", selectedValues);
        },
      });
    }
    setFilters(newFilters);
  }, [splitGrantDetails, formik.values.metaData?.filters]);

  useEffect(() => {
    let data = splitGrantDetails.map((grant) => ({
      ...grant,
      optionsBeforeSplit: grant.optionsGranted,
      optionsAfterSplit: grant.optionsGranted,
      postSplitActiveShares:
        (grant.preSplitActiveShares || 0) * (formik.values.ratio || 1),
      postSplitGrantedShares:
        (grant.preSplitGrantedShares || 0) * (formik.values.ratio || 1),
      postSplitStockExercised:
        (grant.preSplitStockExercised || 0) * (formik.values.ratio || 1),
      postSplitOutstandingShares:
        (grant.preSplitOutstandingShares || 0) * (formik.values.ratio || 1),
      postSplitStrikePrice:
        grant.strikePriceType === StrikePriceType.SHARE_PRICE
          ? grant.strikePrice / (formik.values.ratio || 1)
          : grant.strikePrice,
    }));

    const { filters: activeFilters = {} } = formik.values?.metaData || {};
    if (activeFilters.date) {
      data = data.filter(
        (row) =>
          new Date(row.dateOfGrant).setHours(0, 0, 0, 0) <=
          new Date(activeFilters.date).setHours(0, 0, 0, 0)
      );
    }
    if (activeFilters.planIds && activeFilters.planIds.length > 0) {
      const planIds = activeFilters.planIds.map((p: any) => p.id);
      data = data.filter((row) => planIds.includes(row.planId));
    }
    if (activeFilters.subsidiaryIds && activeFilters.subsidiaryIds.length > 0) {
      const companyIds = activeFilters.subsidiaryIds.map((c: any) => c.id);
      data = data.filter(
        (row) =>
          companyIds.includes(row.companyId) ||
          companyIds.includes(row.subsidiaryCompanyId)
      );
    }
    if (
      activeFilters.employmentStatus &&
      activeFilters.employmentStatus.length > 0
    ) {
      data = data.filter((row) =>
        activeFilters.employmentStatus.includes(row.employeeStatus)
      );
    }
    if (
      activeFilters.employmentType &&
      activeFilters.employmentType.length > 0
    ) {
      data = data.filter((row) =>
        activeFilters.employmentType.includes(row.employmentType)
      );
    }
    if (activeFilters.planType && activeFilters.planType.length > 0) {
      data = data.filter((row) =>
        activeFilters.planType.includes(row.planType)
      );
    }
    if (activeFilters.grantState && activeFilters.grantState.length > 0) {
      data = data.filter((row) =>
        activeFilters.grantState.includes(row.grantState)
      );
    }

    setFilteredRowData(data);
    setPlansEffected(
      [...new Set(data.map((grant) => grant.planId || ""))].length > 0
        ? [...new Set(data.map((grant) => grant.planId || ""))]
        : [...new Set(splitGrantDetails.map((grant) => grant.planId || ""))]
    );
  }, [
    splitGrantDetails,
    formik.values.metaData?.filters,
    formik.values.ratio,
    formik.values.date,
  ]);

  function setPinnedBottomRowData({ api }: { api: any }) {
    const optionsBeforeSplit = filteredRowData.reduce(
      (acc, grant) => acc + (grant.optionsGranted || 0),
      0
    );
    const optionsAfterSplit = filteredRowData.reduce(
      (acc, grant) => acc + (grant.optionsGranted || 0),
      0
    );
    const preSplitGrantedShares = filteredRowData.reduce(
      (acc, grant) => acc + (grant.preSplitGrantedShares || 0),
      0
    );
    const postSplitGrantedShares = filteredRowData.reduce(
      (acc, grant) => acc + (grant.postSplitGrantedShares || 0),
      0
    );
    const preSplitActiveShares = filteredRowData.reduce(
      (acc, grant) => acc + (grant.preSplitActiveShares || 0),
      0
    );
    const postSplitActiveShares = filteredRowData.reduce(
      (acc, grant) => acc + (grant.postSplitActiveShares || 0),
      0
    );

    const outstandingOptions = filteredRowData.reduce(
      (acc, grant) => acc + (grant.outstandingOptions || 0),
      0
    );

    api.setPinnedBottomRowData([
      {
        identifier: "Total",
        employeeName: "",
        optionsBeforeSplit,
        optionsAfterSplit,
        strikePrice: 0,
        conversionMethodology: filteredRowData[0]?.conversionMethodology || "",
        postSplitStrikePrice: 0,
        strikePriceType: "",
        outstandingOptions,
        preSplitGrantedShares,
        postSplitGrantedShares,
        preSplitActiveShares,
        postSplitActiveShares,
        dateOfGrant: "",
        isLastPinnedRow: true,
      },
    ]);
  }
  return (
    <>
      {filters.length > 0 && (
        <GenericFilterBar
          filters={filters}
          disableUpdate={formik.values.state === "APPROVED"}
        />
      )}
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: `${Math.max(
              400,
              (filteredRowData.length >= 10 ? 10 : filteredRowData.length + 3) *
                60
            )}px`,
          }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
        >
          <AgGridReact
            tooltipShowDelay={0}
            getRowStyle={(params) => ({
              background: params.data.isLastPinnedRow ? "#f0f0f0" : "",
              fontWeight: params.data.isLastPinnedRow ? "normal" : "normal",
              color: params.data.isLastPinnedRow ? "black" : "",
            })}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            defaultColDef={defaultColDef}
            suppressRowTransform={true}
            animateRows={true}
            onFilterChanged={setPinnedBottomRowData}
            onRowDataUpdated={setPinnedBottomRowData}
            pagination={false}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          />
        </Box>
      </HStack>
    </>
  );
}

function SplitDocumentsSection({
  formik,
  disableForm,
  mode,
}: {
  formik: any;
  disableForm: boolean;
  mode: "AddSplit" | "EditSplit" | "ApproveSplit";
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { values, setFieldValue } = formik;
  const uploadedFile: UploadedFile | null =
    values.settings?.templateFile || null;
  const backendFileLink = uploadedFile?.s3Url || undefined;

  // Helper to convert file to UploadedFile type
  const fileToUploadedFile = async (file: File): Promise<UploadedFile> => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      base64,
      isUserUploaded: true,
    };
  };

  // File upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const uploaded = await fileToUploadedFile(file);
      setFieldValue("settings.templateFile", uploaded);
    }
  };

  // Remove file handler
  const handleRemoveFile = () => {
    setFieldValue("settings.templateFile", null);
  };

  return (
    <VStack className="gap-4 pt-4 lg:w-[700px]">
      <VStack className="w-full">
        <HStack className="items-center justify-between w-full">
          <Label className="mr-10 text-sm font-normal whitespace-nowrap">
            Split Letter Generation
          </Label>
          <Switch
            checked={values.settings?.generateTransactionDocuments}
            onChange={() =>
              setFieldValue(
                "settings.generateTransactionDocuments",
                !values.settings?.generateTransactionDocuments
              )
            }
            value={values.settings?.generateTransactionDocuments}
            disabled={disableForm}
            className={`${disableForm ? "cursor-not-allowed" : ""}`}
          />
        </HStack>
        <p className="text-xs font-semi-medium text-gray-light">
          Enabling this option will generate split letters for employees or
          grants affected by the split event
        </p>
      </VStack>

      {values.settings?.generateTransactionDocuments && (
        <VStack className="gap-4">
          {/* Grant/Employee Level Selection */}
          <HStack className="gap-6 items-center">
            <Label className="text-sm font-normal">
              Letter Generation Level
            </Label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="splitLetterLevel"
                value="grant"
                checked={
                  values.settings?.useGrantRelatedDocumentTemplate === true
                }
                disabled={disableForm}
                onChange={() => {
                  setFieldValue(
                    "settings.useGrantRelatedDocumentTemplate",
                    true
                  );
                  setFieldValue(
                    "settings.useEmployeeRelatedDocumentTemplate",
                    false
                  );
                }}
              />
              Grant Level
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="splitLetterLevel"
                value="employee"
                checked={
                  values.settings?.useGrantRelatedDocumentTemplate === false
                }
                disabled={disableForm}
                onChange={() => {
                  setFieldValue(
                    "settings.useGrantRelatedDocumentTemplate",
                    false
                  );
                  setFieldValue(
                    "settings.useEmployeeRelatedDocumentTemplate",
                    true
                  );
                }}
              />
              Employee Level
            </label>
          </HStack>

          {/* File Upload/Display */}
          {!uploadedFile && (
            <HStack className="gap-4">
              <ButtonPrimary1
                className={`px-4 py-2 ${
                  disableForm ? "cursor-not-allowed" : ""
                }`}
                onClick={() => {
                  if (disableForm) return;
                  fileInputRef.current?.click();
                }}
                disabled={disableForm}
                type="button"
              >
                Upload Split Letter Template
              </ButtonPrimary1>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={(e) => handleFileUpload(e)}
              />
            </HStack>
          )}

          {values.settings?.generateTransactionDocuments &&
            (formik.touched.settings?.templateFile ||
              formik.errors.settings?.templateFile) && (
              <Error text={formik.errors.settings?.templateFile} />
            )}

          {/* Show uploaded file (new or from backend) */}
          {uploadedFile && (
            <HStack className="gap-4 items-center">
              <a
                href={
                  uploadedFile.base64 ? uploadedFile.base64 : backendFileLink
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
                download={uploadedFile.name}
              >
                {uploadedFile.name || "View Existing Split Letter"}
              </a>
              {!disableForm && (
                <button
                  onClick={handleRemoveFile}
                  className="text-red-500 hover:text-red-700"
                  type="button"
                >
                  <Icon icon="mdi:delete" width={20} height={20} />
                </button>
              )}
            </HStack>
          )}
        </VStack>
      )}
    </VStack>
  );
}

function SplitNotificationSettings({
  formik,
  disableForm,
}: {
  formik: any;
  disableForm: boolean;
}) {
  return (
    <VStack className="gap-3 lg:w-[700px]">
      <VStack className="w-full">
        <HStack className="items-center justify-between w-full">
          <Label className="mr-10 text-sm font-normal whitespace-nowrap">
            Notify Email to Employees
          </Label>
          <Switch
            checked={formik.values.settings?.notifyEmployees}
            onChange={() =>
              formik.setFieldValue(
                "settings.notifyEmployees",
                !formik.values.settings?.notifyEmployees
              )
            }
            value={formik.values.settings?.notifyEmployees}
            disabled={disableForm}
            className={`${disableForm ? "cursor-not-allowed" : ""}`}
          />
        </HStack>
        <p className="text-xs font-semi-medium text-gray-light">
          Send split notification emails to employees with affected grants.
          Emails will be sent regardless of Email Settings
        </p>
      </VStack>
      <VStack className="w-full">
        <HStack className="items-center justify-between w-full">
          <Label className="mr-10 text-sm font-normal whitespace-nowrap">
            Notify on Employee Portal when Split is Approved
          </Label>
          <Switch
            checked={formik.values.settings?.notifyOnEmployeePortal}
            onChange={(e) =>
              formik.setFieldValue(
                "settings.notifyOnEmployeePortal",
                !formik.values.settings?.notifyOnEmployeePortal
              )
            }
            value={formik.values.settings?.notifyOnEmployeePortal}
            disabled={disableForm}
            className={`${disableForm ? "cursor-not-allowed" : ""}`}
          />
        </HStack>
        <p className="text-xs font-semi-medium text-gray-light">
          Notify employees on the employee portal when the split is approved
        </p>
      </VStack>
    </VStack>
  );
}

export default AddOrEditSplitDetails;
